import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, Provider } from '@angular/core';
import {
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { cloneDeep, isEmpty, isEqual, isNil } from 'lodash';

import { CallListSchema } from '@pwp-common';

import { FormGroupControlValueAccessor } from '../../../../generic/abstract-classes/form-group-control-value-accessor';
import { CommunicationWorkflowConfigEditorFormType } from '../../../call-list-communication-workflow-config/editor-output/communication-workflow-config-editor-form-type';
import { ServiceCommandEditorOutput } from '../../../service-operation-editor/editor-output/service-command-editor-output';
import { CallListEditorFormType } from '../editor-output/call-list-editor-form-type';
import { CallListEditorOutput } from '../editor-output/call-list-editor-output';
import { defaultCallListEditorOutput } from '../editor-output/default-call-list-editor-output';

const VALUE_ACCESSOR: Provider = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => CallListEditorComponent),
  multi: true,
};
const VALIDATOR: Provider = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => CallListEditorComponent),
  multi: true,
};
@UntilDestroy()
@Component({
  selector: 'app-call-list-editor',
  templateUrl: './call-list-editor.component.html',
  styleUrls: ['./call-list-editor.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [VALUE_ACCESSOR, VALIDATOR],
})
export class CallListEditorComponent extends FormGroupControlValueAccessor<CallListEditorOutput, CallListEditorOutput> {
  /////////////////////////////////////////////////////////////////////////////////////////////
  // Variables
  /////////////////////////////////////////////////////////////////////////////////////////////
  public baseObj: CallListEditorOutput | undefined = undefined;

  ////////////////////////////////////////////////////////////////////////
  // Lifecycle
  ////////////////////////////////////////////////////////////////////////
  constructor(public changeDetectorRef: ChangeDetectorRef) {
    super(changeDetectorRef);
  }

  ///////////////////////////////////////////////////////////////////////////////////////////
  // Form Controls
  ///////////////////////////////////////////////////////////////////////////////////////////
  public get displayName() {
    return this.form.get(CallListSchema.displayName) as UntypedFormControl;
  }

  public get description() {
    return this.form.get(CallListSchema.description) as UntypedFormControl;
  }

  public get calleeEntities() {
    return this.form.get(CallListSchema.calleeEntities) as UntypedFormControl;
  }

  public get offerConfig() {
    return this.form.get(CallListSchema.offerConfig) as UntypedFormControl;
  }

  public get ringTime() {
    return this.offerConfig.get('ringTime') as UntypedFormControl;
  }

  public get callerId() {
    return this.offerConfig.get('callerId') as UntypedFormControl;
  }

  public get communicationWorkflowConfig() {
    return this.form.get('communicationWorkflowConfig') as UntypedFormControl;
  }

  public get onNoUserLeftToDialModIncCallCommands() {
    return this.form.get(CallListSchema.onNoUserLeftToDialModIncCallCommands) as UntypedFormControl;
  }

  public get isCallFlowTypeAdvanced() {
    const value: CommunicationWorkflowConfigEditorFormType = this.communicationWorkflowConfig?.value;

    /**
     * If local state is specified, then defer to that.
     */
    if (value?.callFlowType) {
      return value?.callFlowType === 'advanced';
    }

    return !isEmpty(value?.communicationWorkflowId);
  }

  public get showIfUnansweredConfig(): boolean {
    const currentCommands = this.onNoUserLeftToDialModIncCallCommands?.value?.commands ?? [];
    const defaultCommands = CallListSchema.Defaults.onNoUserLeftToDialModIncCallCommands.map((command) =>
      this.serializeCommand(command),
    );

    return !isEqual(currentCommands, defaultCommands);
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Define Form
  /////////////////////////////////////////////////////////////////////////////////////////////
  public defineForm() {
    const formConfig = {} as any;
    formConfig[CallListSchema.displayName] = new UntypedFormControl(undefined, [
      Validators.required,
      Validators.minLength(3),
      Validators.maxLength(50),
    ]);
    formConfig[CallListSchema.description] = new UntypedFormControl(undefined, [Validators.maxLength(100)]);
    formConfig[CallListSchema.calleeEntities] = new UntypedFormControl([], [Validators.maxLength(50)]);
    formConfig[CallListSchema.offerConfig] = new UntypedFormGroup({
      ringTime: new UntypedFormControl(undefined),
      callerId: new UntypedFormControl(undefined),
    });
    formConfig[CallListSchema.onNoUserLeftToDialModIncCallCommands] = new UntypedFormControl(
      cloneDeep(defaultCallListEditorOutput.onNoUserLeftToDialModIncCallCommands),
    );
    formConfig.communicationWorkflowConfig = new UntypedFormControl(undefined);
    this.form = new UntypedFormGroup(formConfig);
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Parse Value Change
  /////////////////////////////////////////////////////////////////////////////////////////////
  public parseValueChange(value: CallListEditorOutput): CallListEditorOutput {
    const result = cloneDeep(this.baseObj ?? defaultCallListEditorOutput);
    result.description = this.description.value || result.description;
    result.displayName = this.displayName.value || result.displayName;
    result.calleeEntities = this.calleeEntities.value || result.calleeEntities;
    result.offerConfig.callerId = this.callerId.value || result.offerConfig.callerId;
    result.offerConfig.ringTime = this.ringTime.value || result.offerConfig.ringTime;
    result.onNoUserLeftToDialModIncCallCommands =
      this.onNoUserLeftToDialModIncCallCommands.value || result.onNoUserLeftToDialModIncCallCommands;

    if (this.isCallFlowTypeAdvanced) {
      /**
       * remove the local state.
       */
      const { callFlowType, ...communicationWorkflowConfig } =
        this.communicationWorkflowConfig.value || result.communicationWorkflowConfig;
      result.communicationWorkflowConfig = communicationWorkflowConfig;
    } else {
      result.communicationWorkflowConfig = undefined;
    }
    return result;
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Write Value
  /////////////////////////////////////////////////////////////////////////////////////////////

  public writeValue(value: CallListEditorOutput | undefined | null) {
    /**
     * Preserve any properties of the input, if it is specified (such as the id)
     */
    if (isNil(value)) return;

    this.baseObj = cloneDeep(value ?? defaultCallListEditorOutput);

    const formValue: CallListEditorFormType = {
      description: this.baseObj?.description,
      displayName: this.baseObj?.displayName,
      calleeEntities: this.baseObj.calleeEntities,
      offerConfig: this.baseObj?.offerConfig,
      onNoUserLeftToDialModIncCallCommands: this.baseObj.onNoUserLeftToDialModIncCallCommands,
      communicationWorkflowConfig: this.baseObj.communicationWorkflowConfig,
    };
    this.form.patchValue(formValue, { emitEvent: true });
    this.changeDetectorRef.detectChanges();
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Validation Errors
  /////////////////////////////////////////////////////////////////////////////////////////////
  public makeValidationErrors(): ValidationErrors {
    return {
      'call-list-editor': this.form.value,
    };
  }

  private serializeCommand(command: any): ServiceCommandEditorOutput {
    return (command?.serialize?.() ?? command) as ServiceCommandEditorOutput;
  }
}
